'use client';

import { devWarn } from '@/lib/utils/devLog';
import { ensureTrailingSlash } from '@/lib/utils/ensureTrailingSlash';
import { useRouter } from '@/lib/i18n/navigation';
import { modalActions } from '@/states/modalState';
import { useCallback } from 'react';

/**
 * =============================================================================
 * URL TRIGGER TYPES - STUB
 * =============================================================================
 *
 * These are the event types that can be triggered by NavQuickLinks.
 * In Breitling, these open modals for specific features.
 *
 * TODO: When porting CommonModalProvider, implement these triggers:
 * - 'Get my Breitling NFT' -> Opens NFT modal
 * - 'Campaign Newsletter' -> Opens campaign newsletter signup modal
 * - 'Warranty Extension' -> Opens warranty extension modal (requires auth)
 * - 'Newsletter' -> Dispatches 'newsletter' event to scroll to newsletter section
 *
 * Dependencies needed:
 * - CommonModalProvider (providers/CommonModalProvider)
 * - useUser hook (hooks/useUser) for auth check on Warranty Extension
 * - paths helper (lib/paths) for redirect to login
 */
export type UrlTriggerType =
  | 'Get my Breitling NFT'
  | 'Campaign Newsletter'
  | 'Warranty Extension'
  | 'Newsletter';

/**
 * Event trigger data from Contentful
 * Matches ContentfulEventFragment structure
 */
export interface UrlTriggerEvent {
  __typename?: 'Event';
  sys: { id: string };
  type?: UrlTriggerType | string | null;
  // Additional event fields will be added when porting Event component
  [key: string]: unknown;
}

/**
 * =============================================================================
 * useHandleUrlTrigger - STUB IMPLEMENTATION
 * =============================================================================
 *
 * This hook handles special URL triggers that open modals or dispatch events.
 * Currently stubbed - will log warnings in development until CommonModalProvider is ported.
 *
 * Original implementation in Breitling:
 * - src/components/Cms/NavQuickLinks/components/NavQuickLinkItem/helpers.tsx
 *
 * Required providers/hooks to port:
 * - CommonModalProvider with:
 *   - openCampaignNewsletterModal()
 *   - setGetMyNFTModalStatus()
 *   - setWarrantyExtensionModalStatus()
 * - useUser hook for authentication state
 */
export function useHandleUrlTrigger(urlTrigger?: UrlTriggerEvent | null) {
  const handleUrlTrigger = useCallback(() => {
    if (!urlTrigger?.type) return;

    // ==========================================================================
    // STUB: These triggers need CommonModalProvider to be implemented
    // ==========================================================================

    const triggerType = urlTrigger.type as UrlTriggerType;

    switch (triggerType) {
      case 'Get my Breitling NFT':
        // TODO: setGetMyNFTModalStatus(true) from CommonModalProvider
        devWarn(
          '[NavQuickLinks] URL trigger "Get my Breitling NFT" not implemented.',
          'Requires: CommonModalProvider.setGetMyNFTModalStatus()',
          'Original: src/providers/CommonModalProvider'
        );
        break;

      case 'Campaign Newsletter':
        modalActions.open('newsletter', {
          source: 'nav-quick-link',
          campaign: (urlTrigger.campaign as string) || urlTrigger.type || undefined,
        });
        break;

      case 'Warranty Extension':
        // TODO: Check isAuthenticated from useUser
        // If authenticated: setWarrantyExtensionModalStatus(true)
        // If not: redirect to login with next=currentPath
        devWarn(
          '[NavQuickLinks] URL trigger "Warranty Extension" not implemented.',
          'Requires: CommonModalProvider.setWarrantyExtensionModalStatus() + useUser for auth check',
          'Original: src/providers/CommonModalProvider, src/hooks/useUser'
        );
        break;

      case 'Newsletter':
        modalActions.open('newsletter', { source: 'nav-quick-link' });
        break;

      default:
        devWarn(`[NavQuickLinks] Unknown URL trigger type: ${triggerType}`);
    }
  }, [urlTrigger]);

  return { handleUrlTrigger };
}

/**
 * =============================================================================
 * useAnchor - Anchor/hash navigation handler
 * =============================================================================
 *
 * Handles navigation for anchor links (#section) and external URLs.
 * Supports smooth scrolling to page sections.
 */
export function useAnchor(
  url: string,
  urlTarget: string = '_self',
  disableScrollToTop?: boolean | null
) {
  const router = useRouter();

  const handleAnchorEvent = useCallback(() => {
    if (!url) return;

    // Handle anchor/hash links (e.g., #section-id)
    if (url.startsWith('#')) {
      const element = document.getElementById(url.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    // Handle external URLs
    if (url.startsWith('http://') || url.startsWith('https://') || urlTarget === '_blank') {
      window.open(url, urlTarget, urlTarget === '_blank' ? 'noopener,noreferrer' : undefined);
      return;
    }

    // Handle internal navigation
    router.push(ensureTrailingSlash(url));

    // Scroll to top unless disabled
    if (!disableScrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [url, urlTarget, disableScrollToTop, router]);

  return { handleAnchorEvent };
}

/**
 * =============================================================================
 * useHandleQuickLink - Combined navigation handler
 * =============================================================================
 *
 * Main hook for NavQuickLink items. Handles both URL triggers (modals/events)
 * and standard navigation (anchor links, internal routes, external URLs).
 */
export function useHandleQuickLink(
  url: string,
  urlTarget: string = '_self',
  urlTrigger?: UrlTriggerEvent | null,
  disableNavigationScrollToTop?: boolean | null
) {
  const { handleAnchorEvent } = useAnchor(url, urlTarget, disableNavigationScrollToTop);
  const { handleUrlTrigger } = useHandleUrlTrigger(urlTrigger);

  const handleQuickLink = useCallback(() => {
    // If there's a URL trigger (modal/event), handle that instead of navigation
    if (urlTrigger?.type) {
      handleUrlTrigger();
    } else {
      handleAnchorEvent();
    }
  }, [urlTrigger, handleUrlTrigger, handleAnchorEvent]);

  return { handleQuickLink };
}
